import NoPeek from "assets/images/icons/nopeek.svg";
import Peek from "assets/images/icons/peek.svg";
import CardWrapper from "components/CardWrapper";
import { notifyError, notifySuccess } from "components/ToastMessage";
import { addToCart, setOrderParams, addVpsAddons, changeProductFromCart } from "features/common/layouts/cartSlice";
import { checkPasswordStrength } from "helpers/helper";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProgressBar, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import MainLayouts from "../../layouts/MainLayouts";
import { useLocation } from "react-router-dom";
import VpsProductSelector from "components/VpsProductSelector";
import VpsAddonsSelector from "components/VpsAddonsSelector";
import { goldenFastAPI, getApiConfig, buildApiUrl } from "features/vps/vpsGoldenFastAPI";
import { useQuery } from "helpers/utils";

const Vps = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const hostnameRef = useRef(null);
  const passwordRef = useRef(null);
  const peekIconRef = useRef(null);
  const query = useQuery(); // Move query declaration here

  // Get cart products to check if product already exists
  const cartProducts = useSelector((state) => state.cart.productsList);

  // Fetch product data from API based on PID
  const fetchProductByPID = async (pid) => {
    if (!pid) return;

    setIsLoadingProduct(true);
    setProductError(null);

    try {
      // Use the same API configuration system as other components
      const config = getApiConfig();
      const apiUrl = buildApiUrl(config.endpoints.addons, { pid });

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
        mode: "cors",
      });
      const result = await response.json();

      if (result.status && result.data && result.data.length > 0) {
        const productData = result.data[0];

        // Set the product data
        setGoldenFastProduct(productData);
        setSelectedProductId(productData.pid);

        // Auto-select group based on product's gid
        if (productData.gid) {
          setSelectedGroupId(productData.gid);
        }

        // Auto-select billing cycle if available and valid
        if (productData.pricing?.IDR) {
          const validCycles = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];
          const availableCycles = Object.keys(productData.pricing.IDR).filter(
            cycle => validCycles.includes(cycle) &&
              productData.pricing.IDR[cycle] !== '-1.00' &&
              productData.pricing.IDR[cycle] !== '0.00' &&
              parseFloat(productData.pricing.IDR[cycle]) > 0
          );

          if (availableCycles.length > 0) {
            // If billingcycle is specified in URL and valid, use it
            if (query.billingcycle && availableCycles.includes(query.billingcycle)) {
              setTermin(query.billingcycle);
            } else {
              // Otherwise use the first available cycle
              setTermin(availableCycles[0]);
            }
          }
        }

        // Notify product change to trigger addons loading and update UI
        // Use setTimeout to ensure state updates are processed
        setTimeout(() => {
          handleProductChange(productData);
        }, 100);

        notifySuccess(`Produk "${productData.name}" berhasil dimuat!`);
      } else {
        setProductError("Produk tidak ditemukan atau data tidak valid");
        notifyError("Produk tidak ditemukan");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      setProductError("Gagal memuat data produk");
      notifyError("Gagal memuat data produk");
    } finally {
      setIsLoadingProduct(false);
    }
  };

  // URL Parameters handling
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("pid");
    const gid = params.get("gid");
    const billingcycle = params.get("billingcycle");

    dispatch(setOrderParams({ pid, billingcycle }));

    // If PID is provided in URL, fetch product data
    if (pid) {
      fetchProductByPID(pid);
    } else {
    // Set group based on URL parameter
    if (gid) {
      setSelectedGroupId(gid);
    }

    // Set product based on URL parameter
    if (pid) {
      setSelectedProductId(pid);
    }

    // Set billing cycle from URL
    if (billingcycle) {
      setTermin(billingcycle);
    }
    }
  }, [location.search, dispatch, query.billingcycle]);

  // GoldenFast System States
  const [selectedGroupId, setSelectedGroupId] = useState('38'); // Default to Dedicated Server
  const [selectedProductId, setSelectedProductId] = useState('');
  const [goldenFastProduct, setGoldenFastProduct] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [termin, setTermin] = useState("annually");
  const [passwordStrength, setPasswordStrength] = useState({
    title: null,
    variant: "danger",
    score: 0,
  });

  // Query and API states
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [productError, setProductError] = useState(null);
  const [addonValidationErrors, setAddonValidationErrors] = useState([]);

  // Check if product already exists in cart
  // const existingProduct = goldenFastProduct ? cartProducts.find(item => item.pid === goldenFastProduct.pid) : null;

  // Check if any product from same group exists in cart
  const existingProductFromSameGroup = goldenFastProduct ? cartProducts.find(item => item.gid === goldenFastProduct.gid && item.pid === goldenFastProduct.pid) : null;

  // Password strength checker
  const changePasswordHandler = () => {
    const password = passwordRef?.current?.value;
    const {
      title = null,
      score = 0,
      variant,
    } = checkPasswordStrength(password) ?? {};
    setPasswordStrength({ title, variant, score: score * 20 });
  };

  // Toggle password visibility
  const toggleVisibilityHandler = () => {
    if (passwordRef.current.type === "password") {
      passwordRef.current.type = "text";
      peekIconRef.current.src = NoPeek;
    } else {
      passwordRef.current.type = "password";
      peekIconRef.current.src = Peek;
    }
  };

  // GoldenFast System Handlers
  const handleGroupChange = (groupId) => {
    setSelectedGroupId(groupId);
    setSelectedProductId(''); // Reset product selection
    setGoldenFastProduct(null);
    setSelectedAddons([]);

    // Reset termin to default when changing group
    setTermin("annually");
  };

  const handleProductChange = (product) => {
    setGoldenFastProduct(product);
    setSelectedAddons([]); // Reset addons when product changes

    if (product && product.pid) {
      setSelectedProductId(product.pid);

      // Update billing cycle if needed
      if (product.pricing?.IDR) {
        const validCycles = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];
        const availableCycles = Object.keys(product.pricing.IDR).filter(
          cycle => validCycles.includes(cycle) &&
            product.pricing.IDR[cycle] !== '-1.00' &&
            product.pricing.IDR[cycle] !== '0.00' &&
            parseFloat(product.pricing.IDR[cycle]) > 0
        );
        if (availableCycles.length > 0 && !availableCycles.includes(termin)) {
          setTermin(availableCycles[0]);
        }
      }

      // Only remove products with same gid AND same pid from cart when switching to different product in same group
      if (product.gid) {
        const productsWithSameGidAndPid = cartProducts.filter(item => item.gid === product.gid && item.pid === product.pid);
        if (productsWithSameGidAndPid.length > 0) {
          // This product already exists in cart, no need to remove anything
          // Just reset addons to show existing ones
          const existingProduct = productsWithSameGidAndPid[0];
          if (existingProduct.vpsaddons) {
            setSelectedAddons(existingProduct.vpsaddons);
          }
        }
      }
    } else {
      setSelectedProductId('');
    }
  };

  const handleAddonsChange = (addons) => {
    setSelectedAddons(addons);

    // Update cart if product exists and hostname/password are filled
    // Only update if this is the same product that's currently selected
    if (existingProductFromSameGroup && goldenFastProduct) {
      const hostname = hostnameRef?.current?.value;
      const password = passwordRef?.current?.value;

      if (hostname && password) {
        // Use setTimeout to ensure addons state is updated first
        setTimeout(() => {
          updateCartWithCurrentData();
        }, 0);
      }
    }
  };

  // Handle addon validation
  const handleAddonValidation = (isValid, errors) => {
    setAddonValidationErrors(errors || []);
  };

  // Update cart with specific termin value (for immediate updates)
  const updateCartWithNewTermin = useCallback((newTermin) => {
    if (!goldenFastProduct || !existingProductFromSameGroup) return;

    const hostname = hostnameRef?.current?.value;
    const password = passwordRef?.current?.value;

    if (!hostname || !password) return;

    // Ensure we're updating the correct product (same gid and pid)
    if (existingProductFromSameGroup.gid !== goldenFastProduct.gid || existingProductFromSameGroup.pid !== goldenFastProduct.pid) {
      return;
    }

    // Calculate prices based on new termin value
    const basePrice = parseFloat(goldenFastProduct?.pricing?.IDR?.[newTermin] || 0);
    const setupFee = parseFloat(goldenFastProduct?.pricing?.IDR?.[goldenFastAPI.convertTerminToFee(newTermin)] || 0);
    const finalSetupFee = (setupFee === -1 || setupFee === -1.00) ? 0 : setupFee;

    // Prepare addons data with proper structure
    const processedAddons = selectedAddons.map(addon => {
      const addonPrice = parseFloat(addon[newTermin] || 0);
      const addonSetupFee = parseFloat(addon[goldenFastAPI.convertTerminToFee(newTermin)] || 0);

      return {
        id: addon.id,
        name: addon.name,
        description: addon.description || '',
        price: addonPrice === -1 ? 0 : addonPrice,
        setupfee: addonSetupFee === -1 ? 0 : addonSetupFee,
        billingcycle: addon.billingcycle || newTermin,
        input_name: addon.input_name || addon.name,
        category: addon.category || 'Others',
        // Add billing cycle specific pricing for cart calculation
        [newTermin]: addon[newTermin] || '0.00',
        [goldenFastAPI.convertTerminToFee(newTermin)]: addon[goldenFastAPI.convertTerminToFee(newTermin)] || '0.00',
        // Add all billing cycles for reference
        monthly: addon.monthly || '0.00',
        quarterly: addon.quarterly || '0.00',
        semiannually: addon.semiannually || '0.00',
        annually: addon.annually || '0.00',
        biennially: addon.biennially || '0.00',
        triennially: addon.triennially || '0.00',
        // Add setup fees for all cycles
        msetupfee: addon.msetupfee || '0.00',
        qsetupfee: addon.qsetupfee || '0.00',
        ssetupfee: addon.ssetupfee || '0.00',
        asetupfee: addon.asetupfee || '0.00',
        bsetupfee: addon.bsetupfee || '0.00',
        tsetupfee: addon.tsetupfee || '0.00'
      };
    });

    const productData = {
      pid: goldenFastProduct?.pid,
      gid: selectedGroupId,
      name: goldenFastProduct?.name,
      type: goldenFastProduct?.type || 'server',
      price: basePrice, // Base recurring price without setup fee
      setupfee: finalSetupFee, // Setup fee separated
      billingcycle: newTermin,
      hostname: hostname,
      rootpw: password,
      configoptions: [],
      vpsaddons: processedAddons,
      customfields: [
        {
          id: 'hostname',
          value: hostname
        },
        {
          id: 'rootpw',
          value: password
        }
      ],
      // Add product pricing for reference
      pricing: goldenFastProduct?.pricing,
      // Add product description
      description: goldenFastProduct?.description || '',
      // Add setup fee information for checkout display
      hasSetupFee: finalSetupFee > 0,
      setupFeeAmount: finalSetupFee
    };

    dispatch(changeProductFromCart(productData));
  }, [goldenFastProduct, selectedGroupId, selectedAddons, dispatch, existingProductFromSameGroup]);

  // Update cart when product, billing cycle, or addons change
  const updateCartWithCurrentData = useCallback(() => {
    if (!goldenFastProduct || !existingProductFromSameGroup) return;

    const hostname = hostnameRef?.current?.value;
    const password = passwordRef?.current?.value;

    if (!hostname || !password) return;

    // Ensure we're updating the correct product (same gid and pid)
    if (existingProductFromSameGroup.gid !== goldenFastProduct.gid || existingProductFromSameGroup.pid !== goldenFastProduct.pid) {
      return;
    }

    // Calculate prices based on current termin
    const basePrice = parseFloat(goldenFastProduct?.pricing?.IDR?.[termin] || 0);
    const setupFee = parseFloat(goldenFastProduct?.pricing?.IDR?.[goldenFastAPI.convertTerminToFee(termin)] || 0);
    const finalSetupFee = (setupFee === -1 || setupFee === -1.00) ? 0 : setupFee;

    // Prepare addons data with proper structure
    const processedAddons = selectedAddons.map(addon => {
      const addonPrice = parseFloat(addon[termin] || 0);
      const addonSetupFee = parseFloat(addon[goldenFastAPI.convertTerminToFee(termin)] || 0);

      return {
        id: addon.id,
        name: addon.name,
        description: addon.description || '',
        price: addonPrice === -1 ? 0 : addonPrice,
        setupfee: addonSetupFee === -1 ? 0 : addonSetupFee,
        billingcycle: addon.billingcycle || termin,
        input_name: addon.input_name || addon.name,
        category: addon.category || 'Others',
        // Add billing cycle specific pricing for cart calculation
        [termin]: addon[termin] || '0.00',
        [goldenFastAPI.convertTerminToFee(termin)]: addon[goldenFastAPI.convertTerminToFee(termin)] || '0.00',
        // Add all billing cycles for reference
        monthly: addon.monthly || '0.00',
        quarterly: addon.quarterly || '0.00',
        semiannually: addon.semiannually || '0.00',
        annually: addon.annually || '0.00',
        biennially: addon.biennially || '0.00',
        triennially: addon.triennially || '0.00',
        // Add setup fees for all cycles
        msetupfee: addon.msetupfee || '0.00',
        qsetupfee: addon.qsetupfee || '0.00',
        ssetupfee: addon.ssetupfee || '0.00',
        asetupfee: addon.asetupfee || '0.00',
        bsetupfee: addon.bsetupfee || '0.00',
        tsetupfee: addon.tsetupfee || '0.00'
      };
    });

    const productData = {
      pid: goldenFastProduct?.pid,
      gid: selectedGroupId,
      name: goldenFastProduct?.name,
      type: goldenFastProduct?.type || 'server',
      price: basePrice, // Base recurring price without setup fee
      setupfee: finalSetupFee, // Setup fee separated
      billingcycle: termin,
      hostname: hostname,
      rootpw: password,
      configoptions: [],
      vpsaddons: processedAddons,
      customfields: [
        {
          id: 'hostname',
          value: hostname
        },
        {
          id: 'rootpw',
          value: password
        }
      ],
      // Add product pricing for reference
      pricing: goldenFastProduct?.pricing,
      // Add product description
      description: goldenFastProduct?.description || '',
      // Add setup fee information for checkout display
      hasSetupFee: finalSetupFee > 0,
      setupFeeAmount: finalSetupFee
    };

    dispatch(changeProductFromCart(productData));
  }, [goldenFastProduct, selectedGroupId, selectedAddons, termin, dispatch, existingProductFromSameGroup]);

  // Calculate total price
  const calculateTotal = () => {
    if (!goldenFastProduct || !goldenFastProduct.pricing) return 0;

    const basePrice = parseFloat(goldenFastProduct.pricing.IDR[termin] || 0);
    const setupFee = parseFloat(goldenFastProduct.pricing.IDR[goldenFastAPI.convertTerminToFee(termin)] || 0);

    const addonsTotal = selectedAddons.reduce((total, addon) => {
      // Get addon price for current billing cycle
      const addonPrice = parseFloat(addon[termin] || 0);
      const addonSetupFee = parseFloat(addon[goldenFastAPI.convertTerminToFee(termin)] || 0);

      // Handle special values that indicate free
      const finalAddonPrice = (addonPrice === -1 || addonPrice === -1.00) ? 0 : addonPrice;
      const finalAddonSetupFee = (addonSetupFee === -1 || addonSetupFee === -1.00) ? 0 : addonSetupFee;

      return total + finalAddonPrice + finalAddonSetupFee;
    }, 0);

    const finalSetupFee = (setupFee === -1 || setupFee === -1.00) ? 0 : setupFee;
    const total = basePrice + finalSetupFee + addonsTotal;

    // Price calculation completed

    return total;
  };

  // Calculate base price only (without setup fee)
  const calculateBasePrice = () => {
    if (!goldenFastProduct || !goldenFastProduct.pricing) return 0;

    const basePrice = parseFloat(goldenFastProduct.pricing.IDR[termin] || 0);

    const addonsTotal = selectedAddons.reduce((total, addon) => {
      // Get addon price for current billing cycle (without setup fee)
      const addonPrice = parseFloat(addon[termin] || 0);

      // Handle special values that indicate free
      const finalAddonPrice = (addonPrice === -1 || addonPrice === -1.00) ? 0 : addonPrice;

      return total + finalAddonPrice;
    }, 0);

    const total = basePrice + addonsTotal;

    // Base price calculation completed

    return total;
  };

  // Calculate setup fee only
  const calculateSetupFee = () => {
    if (!goldenFastProduct || !goldenFastProduct.pricing) return 0;

    const setupFee = parseFloat(goldenFastProduct.pricing.IDR[goldenFastAPI.convertTerminToFee(termin)] || 0);

    const addonsSetupTotal = selectedAddons.reduce((total, addon) => {
      const addonSetupFee = parseFloat(addon[goldenFastAPI.convertTerminToFee(termin)] || 0);

      // Handle special values that indicate free
      const finalAddonSetupFee = (addonSetupFee === -1 || addonSetupFee === -1.00) ? 0 : addonSetupFee;

      return total + finalAddonSetupFee;
    }, 0);

    const finalSetupFee = (setupFee === -1 || setupFee === -1.00) ? 0 : setupFee;

    return finalSetupFee + addonsSetupTotal;
  };

  // Get existing addons for this product
  const existingAddons = existingProductFromSameGroup?.vpsaddons || [];

  // Check if there are new addons to add
  const hasNewAddons = selectedAddons.length > 0;

  // Determine button text and action
  const getButtonText = () => {
    if (!goldenFastProduct) return "Pilih Produk Terlebih Dahulu";

    if (existingProductFromSameGroup) {
      if (hasNewAddons) {
        return `➕ Tambah Addon (${selectedAddons.length})`;
      } else {
        return "✅ Produk Sudah di Keranjang";
      }
    } else {
      return `🛒 Tambah ke Keranjang - ${goldenFastAPI.formatRupiah(calculateTotal())}`;
    }
  };

  const isButtonDisabled = () => {
    if (!goldenFastProduct) return true;
    if (existingProductFromSameGroup && !hasNewAddons) return true;

    const hostname = hostnameRef?.current?.value;
    const password = passwordRef?.current?.value;

    if (!hostname || !password) return true;
    if (passwordStrength.score < 60) return true;

    // Check for addon validation errors
    if (addonValidationErrors.length > 0) return true;

    return false;
  };

  // Add to cart handler
  const addToCartHandler = useCallback(() => {
    if (!goldenFastProduct) {
      notifyError("Silakan pilih produk terlebih dahulu");
      return;
    }

    const hostname = hostnameRef?.current?.value;
    const password = passwordRef?.current?.value;

    if (!hostname || !password) {
      notifyError("Hostname dan password harus diisi");
      return;
    }

    if (passwordStrength.score < 60) {
      notifyError("Password terlalu lemah");
      return;
    }

    // Check for addon validation errors
    if (addonValidationErrors.length > 0) {
      notifyError("Konfigurasi wajib belum lengkap! Silakan lengkapi terlebih dahulu.");
      return;
    }

    const basePrice = parseFloat(goldenFastProduct?.pricing?.IDR?.[termin] || 0);
    const setupFee = parseFloat(goldenFastProduct?.pricing?.IDR?.[goldenFastAPI.convertTerminToFee(termin)] || 0);
    const finalSetupFee = (setupFee === -1 || setupFee === -1.00) ? 0 : setupFee;

    // Prepare addons data with proper structure
    const processedAddons = selectedAddons.map(addon => {
      const addonPrice = parseFloat(addon[termin] || 0);
      const addonSetupFee = parseFloat(addon[goldenFastAPI.convertTerminToFee(termin)] || 0);

      return {
        id: addon.id,
        name: addon.name,
        description: addon.description || '',
        price: addonPrice === -1 ? 0 : addonPrice,
        setupfee: addonSetupFee === -1 ? 0 : addonSetupFee,
        billingcycle: addon.billingcycle || termin,
        input_name: addon.input_name || addon.name,
        category: addon.category || 'Others',
        // Add billing cycle specific pricing for cart calculation
        [termin]: addon[termin] || '0.00',
        [goldenFastAPI.convertTerminToFee(termin)]: addon[goldenFastAPI.convertTerminToFee(termin)] || '0.00',
        // Add all billing cycles for reference
        monthly: addon.monthly || '0.00',
        quarterly: addon.quarterly || '0.00',
        semiannually: addon.semiannually || '0.00',
        annually: addon.annually || '0.00',
        biennially: addon.biennially || '0.00',
        triennially: addon.triennially || '0.00',
        // Add setup fees for all cycles
        msetupfee: addon.msetupfee || '0.00',
        qsetupfee: addon.qsetupfee || '0.00',
        ssetupfee: addon.ssetupfee || '0.00',
        asetupfee: addon.asetupfee || '0.00',
        bsetupfee: addon.bsetupfee || '0.00',
        tsetupfee: addon.tsetupfee || '0.00'
      };
    });

    const productData = {
      pid: goldenFastProduct?.pid,
      gid: selectedGroupId,
      name: goldenFastProduct?.name,
      type: goldenFastProduct?.type || 'server',
      price: basePrice, // Base recurring price without setup fee
      setupfee: finalSetupFee, // Setup fee separated
      billingcycle: termin,
      hostname: hostname, // Ensure this field is set correctly
      rootpw: password,   // Ensure this field is set correctly
      configoptions: [],
      vpsaddons: processedAddons,
      customfields: [
        {
          id: 'hostname',
          value: hostname
        },
        {
          id: 'rootpw',
          value: password
        }
      ],
      // Add product pricing for reference
      pricing: goldenFastProduct?.pricing,
      // Add product description
      description: goldenFastProduct?.description || '',
      // Add setup fee information for checkout display
      hasSetupFee: finalSetupFee > 0,
      setupFeeAmount: finalSetupFee
    };



    if (existingProductFromSameGroup) {
      // Product already exists, just add/update addons

      // Add each addon using addVpsAddons
      processedAddons.forEach(addon => {
        dispatch(addVpsAddons({
          pid: goldenFastProduct?.pid,
          addons: addon
        }));
      });

      notifySuccess("Addon berhasil ditambahkan ke produk yang sudah ada!");
    } else {
      // New product, add to cart
      dispatch(addToCart(productData));
      notifySuccess("Produk berhasil ditambahkan ke keranjang!");
    }
  }, [
    goldenFastProduct,
    selectedGroupId,
    selectedAddons,
    termin,
    passwordStrength.score,
    dispatch,
    existingProductFromSameGroup
  ]);

  return (
    <MainLayouts summary={false}>
      {/* Loading State */}
      {isLoadingProduct && (
        <CardWrapper title="Memuat Produk">
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Memuat data produk...</h5>
            <p className="text-muted">Mohon tunggu sebentar</p>
          </div>
        </CardWrapper>
      )}

      {/* Error State */}
      {productError && (
        <CardWrapper title="Error">
          <div className="text-center py-5">
            <div className="alert alert-danger" role="alert">
              <h5>⚠️ Gagal Memuat Produk</h5>
              <p className="mb-3">{productError}</p>
              <button
                className="btn btn-outline-danger"
                onClick={() => {
                  setProductError(null);
                  if (query.pid) {
                    fetchProductByPID(query.pid);
                  }
                }}
              >
                Coba Lagi
              </button>
            </div>
          </div>
        </CardWrapper>
      )}

      {/* Product Loaded Success Message */}
      {/* {!isLoadingProduct && !productError && goldenFastProduct && query.pid && (
        <CardWrapper title="Produk Dipilih">
          <div className="alert alert-success border-0" style={{ backgroundColor: '#d4edda' }}>
            <div className="d-flex align-items-center">
              <span className="me-3">✅</span>
              <div>
                <strong>Produk berhasil dimuat dari URL!</strong>
                <div className="small text-muted">
                  {goldenFastProduct.name} - {goldenFastAPI.formatRupiah(goldenFastProduct.pricing?.IDR?.[termin] || 0)} per {goldenFastAPI.convertTermin(termin)}
                </div>
              </div>
            </div>
          </div>
        </CardWrapper>
      )} */}

      {/* GoldenFast Product Selector */}
      {!isLoadingProduct && !productError && (
      <VpsProductSelector
        selectedGroupId={selectedGroupId}
        selectedProductId={selectedProductId}
        onGroupChange={handleGroupChange}
        onProductChange={handleProductChange}
      />
      )}

      {/* GoldenFast Addons Selector */}
      {!isLoadingProduct && !productError && (
      <VpsAddonsSelector
        productDetail={goldenFastProduct}
        selectedAddons={selectedAddons}
        onAddonsChange={handleAddonsChange}
        billingCycle={termin}
        existingAddons={existingAddons}
          onValidationChange={handleAddonValidation}
      />
      )}

      {/* Server Configuration */}
      {!isLoadingProduct && !productError && goldenFastProduct && (
        <CardWrapper title="Ringkasaan Server">
          <div className="row">
            {/* Billing Cycle */}
            <div className="col-12 col-md-6 mb-3">
              <h6 className="fw-bold">Periode Langganan</h6>
              <Form.Select
                value={termin}
                onChange={(e) => {
                  const newTermin = e.target.value;
                  setTermin(newTermin);

                  // Update cart if product exists and hostname/password are filled
                  // Only update if this is the same product that's currently selected
                  if (existingProductFromSameGroup && goldenFastProduct) {
                    const hostname = hostnameRef?.current?.value;
                    const password = passwordRef?.current?.value;

                    if (hostname && password) {
                      // Update cart immediately with new termin value
                      updateCartWithNewTermin(newTermin);
                    }
                  }
                }}
                className="form-select"
              >
                {goldenFastProduct?.pricing?.IDR &&
                  Object.keys(goldenFastProduct.pricing.IDR)
                    .filter(cycle => {
                      // Only include valid billing cycles (not setup fees or other suffixes)
                      const validCycles = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially'];
                      return validCycles.includes(cycle) &&
                      goldenFastProduct.pricing.IDR[cycle] !== '-1.00' &&
                      goldenFastProduct.pricing.IDR[cycle] !== '0.00' &&
                        parseFloat(goldenFastProduct.pricing.IDR[cycle]) > 0;
                    })
                    .map(cycle => (
                      <option key={cycle} value={cycle}>
                        {goldenFastAPI.convertTermin(cycle)} - {goldenFastAPI.formatRupiah(goldenFastProduct.pricing.IDR[cycle])}
                      </option>
                    ))
                }
              </Form.Select>
            </div>

            {/* Total Price Display */}
            <div className="col-12 col-md-6 mb-3">
              <h6 className="fw-bold">Total Harga</h6>
              <div className="mb-2">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Harga Produk:</span>
                  <span className="fw-bold">{goldenFastAPI.formatRupiah(parseFloat(goldenFastProduct?.pricing?.IDR?.[termin] || 0))}</span>
                </div>
                {(() => {
                  const addonsTotal = selectedAddons.reduce((total, addon) => {
                    const addonPrice = parseFloat(addon[termin] || 0);
                    const finalAddonPrice = (addonPrice === -1 || addonPrice === -1.00) ? 0 : addonPrice;
                    return total + finalAddonPrice;
                  }, 0);

                  if (addonsTotal > 0) {
                    return (
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Harga Addon:</span>
                        <span className="fw-bold">+ {goldenFastAPI.formatRupiah(addonsTotal)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {calculateSetupFee() > 0 && (
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Setup Fee:</span>
                    <span className="fw-bold">+ {goldenFastAPI.formatRupiah(calculateSetupFee())}</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <span className="fw-bold">Total:</span>
                  <span className="h5 fw-bold text-warning mb-0">{goldenFastAPI.formatRupiah(calculateTotal())}</span>
                </div>
                <div className="mt-1">
                <small className="text-muted">* Harga belum termasuk PPN</small>
                </div>
              </div>
              {/* <small className="text-muted">per {goldenFastAPI.convertTermin(termin)}</small> */}
            </div>

            {/* Hostname */}
            <div className="col-12 col-md-6 mb-3">
              <h6 className="fw-bold">Hostname</h6>
              <input
                ref={hostnameRef}
                type="text"
                className="form-control"
                placeholder="contoh: server01.domain.com"
                onChange={() => {
                  // Update cart if product exists and password is filled
                  // Only update if this is the same product that's currently selected
                  if (existingProductFromSameGroup && goldenFastProduct) {
                    const hostname = hostnameRef?.current?.value;
                    const password = passwordRef?.current?.value;

                    if (hostname && password) {
                      updateCartWithCurrentData();
                    }
                  }
                }}
                required
              />
            </div>

            {/* Root Password */}
            <div className="col-12 col-md-6 mb-3">
              <h6 className="fw-bold">Root Password</h6>
              <div className="input-group">
                <input
                  ref={passwordRef}
                  type="password"
                  className="form-control"
                  placeholder="Min 8 karakter, kombinasi huruf, angka & simbol"
                  onChange={(e) => {
                    changePasswordHandler();

                    // Update cart if product exists and hostname is filled
                    // Only update if this is the same product that's currently selected
                    if (existingProductFromSameGroup && goldenFastProduct) {
                      const hostname = hostnameRef?.current?.value;
                      const password = e.target.value;

                      if (hostname && password) {
                        updateCartWithCurrentData();
                      }
                    }
                  }}
                  required
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={toggleVisibilityHandler}
                >
                  <img ref={peekIconRef} src={Peek} alt="peek.svg" />
                </button>
              </div>
              {passwordStrength.score !== 0 && (
                <div className="mt-2">
                  <ProgressBar
                    variant={passwordStrength.variant}
                    now={passwordStrength.score}
                  />
                  <div className={`text-${passwordStrength.variant} fw-bold small`}>
                    {passwordStrength.title}
                  </div>
                </div>
              )}
            </div>


            {/* Add to Cart Button */}
            <div className="col-12 mt-4 text-center">
              {/* Status indicator for existing product */}
              {/* {existingProduct && (
                <div className="alert alert-info mb-3 border-0" style={{ backgroundColor: '#e3f2fd' }}>
                  <div className="d-flex ">
                    <span className="me-2">✅</span>
                    <div>
                      <strong>Produk sudah ada di keranjang</strong>
                      <div className="small text-muted">
                        {existingAddons.length > 0
                          ? `${existingAddons.length} addon sudah dipilih`
                          : 'Belum ada addon yang dipilih'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )} */}

              {/* Status indicator for new product */}
              {/* {!existingProduct && goldenFastProduct && (
                <div className="alert alert-warning mb-3 border-0" style={{ backgroundColor: '#fff3cd' }}>
                  <div className="d-flex ">
                    <span className="me-2">🆕</span>
                    <div>
                      <strong>Produk baru dipilih</strong>
                      <div className="small text-muted">
                        Klik "Tambah ke Keranjang" untuk menambahkan produk ini
                      </div>
                    </div>
                  </div>
                </div>
              )} */}

              <button
                className="btn btn-lg px-5 py-3"
                onClick={addToCartHandler}
                disabled={isButtonDisabled()}
                style={{
                  backgroundColor: isButtonDisabled() ? '#cccccc' : '#F8B038',
                  borderColor: isButtonDisabled() ? '#cccccc' : '#F8B038',
                  color: 'white',
                  borderRadius: '25px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  boxShadow: isButtonDisabled() ? 'none' : '0 4px 8px rgba(248, 176, 56, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '250px',
                  cursor: isButtonDisabled() ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isButtonDisabled()) {
                    e.target.style.backgroundColor = '#e6a52e';
                    e.target.style.borderColor = '#e6a52e';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(248, 176, 56, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isButtonDisabled()) {
                    e.target.style.backgroundColor = '#F8B038';
                    e.target.style.borderColor = '#F8B038';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 8px rgba(248, 176, 56, 0.3)';
                  }
                }}
              >
                {getButtonText()}
              </button>
              {/* <div className="text-muted small mt-2">
                Secure checkout • 30 hari garansi uang kembali
              </div> */}
            </div>
          </div>
        </CardWrapper>
      )}

      {/* No Product Selected State */}
      {!isLoadingProduct && !productError && !goldenFastProduct && (
        <CardWrapper title="Mulai Pemesanan">
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="display-1 text-primary mb-3">🖥️</div>
              <h4 className="text-secondary">Pilih Produk Untuk Memulai</h4>
              <p className="text-muted">
                Silakan pilih grup produk dan produk yang sesuai dengan kebutuhan Anda di atas.
              </p>
            </div>
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="alert alert-info border-0" style={{ backgroundColor: '#e3f2fd' }}>
                  <h6 className="fw-bold mb-2">💡 Tips Memilih Produk:</h6>
                  <ul className="list-unstyled text-start mb-0 small">
                    <li>• <strong>Dedicated Server:</strong> Solusi untuk aplikasi resource tinggi</li>
                    <li>• <strong>Cloud VPS:</strong> Solusi fleksibel untuk website dan aplikasi</li>
                    <li>• <strong>Colocation:</strong> Untuk server fisik milik sendiri</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardWrapper>
      )}
    </MainLayouts>
  );
};

export default Vps;
